import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, useParams } from "@tanstack/react-router";
import { FileSearch02, Passport, SearchMd } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import {
	AuditLogActionBadge,
	EmptyValue,
	TargetUserCell,
	TruncatedTooltipCellText,
} from "@/components/common";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	LogTableFilters,
	type LogTableFiltersState,
} from "@/components/Molecules/FilterDrawer/LogTableFilters";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TABLE_SEARCH_EMPTY_DESCRIPTION_KEY } from "@/components/Molecules/Table/TableSearchEmptyState";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useLogsFilterParams } from "@/hooks/use-logs-filter-params";
import { useTableSettings } from "@/hooks/use-table-settings";
import {
	type AuditLog,
	auditLogQueryOptions,
	useExportAuditLogs,
} from "@/services/audit-log";
import { organizationQueryOptions } from "@/services/organization";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { formatLongDate } from "@/utils/utils";

// Table settings constants
const TABLE_KEY = "orgAdmin.activity.userLogs";
const DEFAULT_COLUMN_ORDER = [
	"id",
	"date",
	"eventType",
	"user.displayName",
	"entityId",
	"organization.name",
	"userIpAddress",
	"userAgent",
	"source",
	"action",
	"fieldName",
	"oldValue",
	"newValue",
];
const DEFAULT_SORTING = [{ id: "date", desc: false }];
const DEFAULT_PAGE_SIZE = 10;

const normalizeColumnOrder = (columnOrder?: string[]) => {
	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...DEFAULT_COLUMN_ORDER];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter((columnId) =>
		allowedColumns.has(columnId),
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults];
};

// Serializable filter state for persistence (excludes date ranges)
interface PersistedFilters {
	eventTypes?: string[];
	sources?: string[];
}
const DEFAULT_FILTERS: PersistedFilters = {
	eventTypes: [],
	sources: [],
};

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/activity/logs/(userLogs)/userLogs",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return z
			.object({
				filterOpen: z.boolean().optional(),
			})
			.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/activity/logs/(userLogs)/userLogs",
	});
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [filters, setFilters] = useState<LogTableFiltersState>({
		eventTypes: [],
		sources: [],
		timestamp: undefined,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["date"],
		"OrderBy.Descending": false,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (search, filters, page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (tableSettings.filters) {
			setFilters((prev) => ({
				...prev,
				eventTypes: tableSettings.filters.eventTypes ?? [],
				sources: tableSettings.filters.sources ?? [],
			}));
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.filters,
		tableSettings.pageSize,
	]);

	const sortFieldMapping: Record<string, string[]> = {
		id: ["Id"],
		date: ["Date"],
		eventType: ["Action", "EntityType"],
		"user.displayName": ["User.DisplayName"],
		entityId: ["entityId"],
		"organization.name": ["Organization.Name"],
		userIpAddress: ["UserIpAddress"],
		userAgent: ["UserAgent"],
		source: ["Source"],
		action: ["Action"],
		fieldName: ["FieldName"],
		oldValue: ["OldValue"],
		newValue: ["NewValue"],
	};
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportAuditLogs();
	const filterParams = useLogsFilterParams(filters);

	const auditLogsCount = useQuery(
		auditLogQueryOptions.count({
			EntityTypes: "OrganizationUser",
			OrganizationId: params.orgId,
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const unFilteredAuditLogsCount = useQuery(
		auditLogQueryOptions.count({
			EntityTypes: "OrganizationUser",
			OrganizationId: params.orgId,
		}),
	);

	const allAuditLogs = useQuery(
		auditLogQueryOptions.list(startIndex, pageSize, {
			EntityTypes: "OrganizationUser",
			OrganizationId: params.orgId,
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const data = useMemo(() => {
		return allAuditLogs.data ?? [];
	}, [allAuditLogs.data]);

	const pageCount = useMemo(() => {
		if (!auditLogsCount.data) return 0;
		return Math.ceil(auditLogsCount.data / pageSize);
	}, [auditLogsCount.data, pageSize]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await allAuditLogs.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = auditLogsCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		// Transform column IDs for export - eventType maps to action and entityType
		const exportColumns = visibleColumns?.flatMap((col) =>
			col === "eventType" ? ["action", "entityType"] : [col],
		);

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					EntityTypes: "OrganizationUser",
					SearchTerm: searchTerm,
					...filterParams,
					...sortParams,
					columns: exportColumns?.join(","),
				},
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("org_user_audit_logs"));
				},
			},
		);
	};

	const handleRowClick = (auditLog: AuditLog) => {
		console.log("auditLog", auditLog);
	};

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (search: string) => {
		setSearchTerm(search);
		setPageIndex(0);
		// Save search term for persistence
		saveSearchTerm(search);
	};

	const handleFilterChange = (newFilters: LogTableFiltersState) => {
		setFilters(newFilters);
		// Save filters for persistence (only serializable fields)
		saveFilters({
			eventTypes: newFilters.eventTypes,
			sources: newFilters.sources,
		});
	};

	const auditLogColumns: ColumnDef<AuditLog>[] = [
		{
			id: "id",
			accessorKey: "id",
			header: t("org-admin.activity.logs.columns.eventId"),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			cell: ({ row }) => {
				const user = row.original;
				return user.id ? (
					<div className="flex items-center gap-3">
						<Avatar
							alt={user.id}
							placeholder={<FileSearch02 className="size-6 text-gray-400" />}
							size="md"
						/>
						<div className="whitespace-nowrap">
							<p className="text-sm font-medium">
								{<CopyableId value={user.id} />}
							</p>
						</div>
					</div>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "date",
			accessorKey: "date",
			header: t("org-admin.activity.logs.columns.timestamp"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.date ? (
							formatLongDate(user.date, { includeTime: true })
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "eventType",
			accessorKey: "eventType",
			header: t("org-admin.activity.logs.columns.eventType"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium text-primary">
						{user.action && user.entityType ? (
							`${user.action} ${user.entityType}`
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "user.displayName",
			accessorKey: "user.displayName",
			header: t("org-admin.activity.logs.columns.actorUser"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium text-primary">
						{user.user?.displayName ? (
							<span className="text-sm font-medium text-primary">
								{user.user?.displayName}
							</span>
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "entityId",
			accessorKey: "entityId",
			header: t("org-admin.activity.logs.columns.targetUser"),
			enableSorting: true,
			cell: ({ row }) => {
				const auditLog = row.original;
				return (
					<TargetUserCell
						auditLog={auditLog}
						userQueryOptions={
							auditLog.entityType === "User" && auditLog.entityId
								? organizationQueryOptions.users.detail(
										params.orgId,
										auditLog.entityId,
									)
								: undefined
						}
					/>
				);
			},
		},
		{
			id: "organization.name",
			accessorKey: "organization.name",
			header: t("org-admin.activity.logs.columns.organizationName"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium text-primary">
						{user.organization?.name ? (
							<span className="text-sm font-medium text-primary">
								{user.organization?.name}
							</span>
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "userIpAddress",
			accessorKey: "userIpAddress",
			header: t("org-admin.activity.logs.columns.actorIpAddress"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.userIpAddress ? (
							<span className="text-sm font-medium">{user.userIpAddress}</span>
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "userAgent",
			accessorKey: "userAgent",
			header: t("org-admin.activity.logs.columns.actorUserAgent"),
			enableSorting: true,
			cell: ({ row }) => (
				<TruncatedTooltipCellText value={row.original.userAgent} />
			),
		},
		{
			id: "source",
			accessorKey: "source",
			header: t("org-admin.activity.logs.columns.source"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.source ? (
							<span className="text-sm font-medium">{user.source}</span>
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "action",
			accessorKey: "action",
			header: t("org-admin.activity.logs.columns.action"),
			enableSorting: true,
			cell: ({ row }) => <AuditLogActionBadge action={row.original.action} />,
		},
		{
			id: "fieldName",
			accessorKey: "fieldName",
			header: t("org-admin.activity.logs.columns.fieldName"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.fieldName ?? <EmptyValue />}
					</span>
				);
			},
		},
		{
			id: "oldValue",
			accessorKey: "oldValue",
			header: t("org-admin.activity.logs.columns.oldValue"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.oldValue ?? <EmptyValue />}
					</span>
				);
			},
		},
		{
			id: "newValue",
			accessorKey: "newValue",
			header: t("org-admin.activity.logs.columns.newValue"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.newValue ?? <EmptyValue />}
					</span>
				);
			},
		},
	];

	const noLogs = (isSearchEmpty: boolean) => {
		const searchEmptyTitle = t(
			"admin.activity.logs.table.empty.noSearchResults",
		);

		return (
			<div className="flex flex-col items-center justify-center">
				<div className="relative flex items-center justify-center w-[480px] h-[480px]">
					<div className="relative flex flex-col items-center justify-center gap-4 text-center">
						{isSearchEmpty ? (
							<div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
								<SearchMd className="size-6 text-gray-500" />
							</div>
						) : (
							<Illustration type="cloud" size="sm">
								<Passport className="size-6" />
							</Illustration>
						)}
						<div className="flex flex-col gap-1">
							{isSearchEmpty ? (
								searchEmptyTitle ? (
									<h2 className="font-semibold text-primary text-md">
										{searchEmptyTitle}
									</h2>
								) : null
							) : (
								<h2 className="font-semibold text-primary text-md">
									{"No Logs"}
								</h2>
							)}
							<p className="text-sm text-tertiary">
								{isSearchEmpty
									? t(TABLE_SEARCH_EMPTY_DESCRIPTION_KEY)
									: "Logs will appear here when they are created"}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	};

	useEffect(() => {
		if (!pageCount) return;

		const prefetchRange = 1;

		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					auditLogQueryOptions.list(nextStartIndex, pageSize, {
						EntityTypes: "OrganizationUser",
						OrganizationId: params.orgId,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		queryClient,
		params.orgId,
		filterParams,
		sortParams,
	]);

	if (allAuditLogs.isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{"Failed to load logs"}
						</h3>
						<p className="text-sm text-tertiary mt-1">
							{allAuditLogs.error?.message}
						</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-0">
			<CustomTableWrapper
				tableKey={TABLE_KEY}
				// Data
				data={data}
				columns={auditLogColumns}
				// Display
				title={t("admin.users.tabs.table.title")}
				showHeaderPadding={false}
				zebraStriping
				// Loading States
				isLoading={allAuditLogs.isLoading}
				isFetching={allAuditLogs.isFetching}
				isRefreshing={isRefreshing}
				onRefresh={handleRefresh}
				// Pagination
				paginationMode="server"
				externalPaginationState={{ pageSize, pageIndex }}
				onPaginationChange={handlePaginationChange}
				pageCount={pageCount}
				rowCount={auditLogsCount.data}
				totalRowCount={unFilteredAuditLogsCount.data}
				// Sorting
				sortFieldMapping={sortFieldMapping}
				defaultSortField="date"
				onSortChange={({ fields, descending }) => {
					setSortParams({
						"OrderBy.Fields": fields,
						"OrderBy.Descending": descending,
					});
				}}
				// Filtering
				filterPlaceholder={t(
					"org-admin.activity.userLogs.table.search.placeholder",
				)}
				filterValue={searchTerm}
				onDebouncedFilterChange={handleDebouncedSearchChange}
				filtersTrailingContent={
					<LogTableFilters value={filters} onChange={handleFilterChange} />
				}
				// Table Actions
				enableTableActions
				filterLayout="bar"
				filterBarType="workspace"
				filterBarClassName="pt-0"
				hasActiveFilters={Object.keys(filterParams).length > 0}
				onExportClick={handleExport}
				isExportLoading={exportMutation.isPending}
				// Features
				enableColumnReordering
				onRowClick={handleRowClick}
				enableColumnResizing
				// Persistence - Initial values from saved settings
				initialSortingState={tableSettings.sorting}
				initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
				initialColumnVisibility={tableSettings.columnVisibility}
				settingsLoaded={settingsLoaded}
				// Persistence - Save callbacks
				onSortingStateChange={saveSorting}
				onColumnOrderChange={(order) => {
					saveColumnOrder(normalizeColumnOrder(order));
				}}
				onColumnVisibilityChange={saveColumnVisibility}
			/>

			{!allAuditLogs.isLoading && !allAuditLogs.isFetching && data?.length <= 0
				? noLogs(
						(unFilteredAuditLogsCount.data ?? 0) > 0 &&
							(auditLogsCount.data ?? 0) === 0,
					)
				: null}
			<Outlet />
		</div>
	);
}
